import { Check, X } from '@phosphor-icons/react';
import type { QuickstartItemDto } from '@reelplicate/api-client/model';
import { Button, cn, IconButton, Link, ProgressBar } from '@reelplicate/ui';
import { useId } from 'react';
import { Link as RouterLink } from 'react-router';

/**
 * Figma "Onboarding Checklist" (106:182): first-run tasks in a dismissible
 * card - progress bar with "n of m", then Checklist Items (106:53). Done
 * collapses to a check and a muted title; the current (first open) task
 * expands with a one-line why and its action; the rest show how long they take.
 * The items come from the API (`quickstart_items`), so they can change without
 * a deploy.
 */
export function OnboardingChecklist({
  tasks,
  onDismiss,
}: {
  tasks: QuickstartItemDto[];
  onDismiss: () => void;
}) {
  const titleId = useId();
  const done = tasks.filter((t) => t.done).length;
  const current = tasks.find((t) => !t.done);

  return (
    <section
      aria-labelledby={titleId}
      className="bg-bg-surface border-border-subtle shadow-md flex flex-col rounded-xl border"
    >
      <header className="flex flex-col gap-2.5 pt-5 pr-4 pb-4 pl-6">
        <div className="flex items-center">
          <h2 id={titleId} className="text-heading-h4 text-text-primary flex-1">
            Get started with Reelplicate
          </h2>
          <IconButton
            icon={X}
            size="sm"
            variant="ghost"
            aria-label="Dismiss"
            onClick={onDismiss}
          />
        </div>
        <div className="flex items-center gap-3">
          <ProgressBar
            size="sm"
            value={done}
            max={tasks.length}
            aria-label="Setup progress"
            className="flex-1"
          />
          <p className="text-data-md text-text-secondary">
            {done} of {tasks.length}
          </p>
        </div>
      </header>

      <ol className="flex flex-col gap-0.5 px-3 pb-3">
        {tasks.map((task) => {
          const isCurrent = task === current;
          return (
            <li
              key={task.key}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'flex items-start gap-3 rounded-md p-3',
                isCurrent && 'bg-bg-subtle',
              )}
            >
              <Marker done={task.done} current={isCurrent} />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p
                  className={cn(
                    'text-body-md-strong',
                    task.done ? 'text-text-tertiary' : 'text-text-primary',
                  )}
                >
                  {task.title}
                  {task.done && <span className="sr-only"> (done)</span>}
                </p>
                {isCurrent && task.description && (
                  <p className="text-body-sm text-text-secondary">{task.description}</p>
                )}
                {isCurrent && task.action && (
                  <Button asChild size="sm" variant="outline" className="self-start">
                    <RouterLink to={task.action.path}>{task.action.label}</RouterLink>
                  </Button>
                )}
              </div>
              <p className="text-caption text-text-tertiary" aria-hidden={task.done}>
                {task.done
                  ? 'Done'
                  : task.durationMinutes != null && `${task.durationMinutes} min`}
              </p>
            </li>
          );
        })}
      </ol>

      <div className="flex justify-center pt-1 pb-4">
        <Link asChild tone="neutral" size="sm">
          <button type="button" onClick={onDismiss}>
            Skip setup for now
          </button>
        </Link>
      </div>
    </section>
  );
}

function Marker({ done, current }: { done?: boolean; current: boolean }) {
  if (done) {
    return (
      <span className="bg-brand-strong flex size-[22px] shrink-0 items-center justify-center rounded-full">
        <Check aria-hidden weight="bold" className="text-icon-inverse size-3" />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'bg-bg-surface size-[22px] shrink-0 rounded-full',
        current
          ? 'border-brand-strong border-2 shadow-[0_0_0_4px_var(--color-brand-subtle-hover)]'
          : 'border-border-strong border-[1.5px]',
      )}
    />
  );
}
